/**
 * Phase 5 — WPC & Billing tab (WO detail).
 * WPC list with the deduction waterfall, RA bill raising (compliance-gated)
 * and subcontractor RA bill approval.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, FileCheck2, Receipt } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';

const WPC_BADGE = {
  draft: 'bg-slate-100 text-slate-600', approved: 'bg-blue-50 text-blue-700',
  billed: 'bg-emerald-50 text-emerald-700', paid: 'bg-emerald-100 text-emerald-800',
};
const BILL_BADGE = {
  'Pending Approval': 'bg-amber-50 text-amber-700', Approved: 'bg-emerald-50 text-emerald-700',
  Rejected: 'bg-red-50 text-red-700', Paid: 'bg-emerald-100 text-emerald-800',
};

export default function WOBillingTab({ wo, onChanged }) {
  const [wpcs, setWpcs] = useState(null);
  const [bills, setBills] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [w, b] = await Promise.all([
        apiGet('/wpc', { work_order_id: wo.id }),
        apiGet('/ra-bills'),
      ]);
      setWpcs(w?.items || []);
      setBills((Array.isArray(b) ? b : (b?.items || [])).filter((x) => x.work_order_id === wo.id));
    } catch { setWpcs([]); }
  }, [wo.id]);
  useEffect(() => { load(); }, [load]);

  const raiseBill = async (wpc) => {
    setBusy(true);
    try {
      const bill = await apiPost('/ra-bills/from-wpc', { wpc_id: wpc.id });
      toast.success(`${bill.bill_no} raised`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to raise bill'); }
    finally { setBusy(false); }
  };

  const billAct = async (bill, path, body) => {
    setBusy(true);
    try {
      await apiPost(`/ra-bills/${bill.id}/${path}`, body || {});
      toast.success('Done');
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  // Automation #3 — draft the next subcontractor RA bill from BOQ earned
  // value minus previously billed (alternative to the per-WPC flow).
  const draftFromBoq = async () => {
    setBusy(true);
    try {
      const r = await apiPost(`/automation/draft-ra-bill?project_id=${wo.project_id}${wo.subcontractor_id ? `&subcontractor_id=${wo.subcontractor_id}` : ''}`);
      if (r.ok) toast.success(`${r.bill.ra_bill_no} drafted — ₹${Number(r.bill.gross_amount || 0).toLocaleString('en-IN')} from BOQ earned value`);
      else toast.info(r.reason || 'No new earned value to bill');
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to draft bill'); }
    finally { setBusy(false); }
  };

  if (wpcs === null) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-5" data-testid="wo-billing-tab">
      <div>
        <div className="kpi-overline flex items-center gap-1.5 mb-2"><FileCheck2 size={11} /> Work Progress Certificates ({wpcs.length})</div>
        <table className="w-full dense-table">
          <thead><tr><th>WPC No</th><th>Date</th><th>Source</th><th className="text-right">Gross</th><th className="text-right">Net Payable</th><th className="text-right">Cum. Certified</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {wpcs.length === 0 && <tr><td colSpan="8" className="text-center py-8 text-slate-400 text-xs">No WPCs yet — approve a measurement book or certify a milestone</td></tr>}
            {wpcs.map((w) => (
              <React.Fragment key={w.id}>
                <tr className="cursor-pointer" onClick={() => setExpanded(expanded === w.id ? null : w.id)} data-testid={`wpc-row-${w.wpc_no}`}>
                  <td className="font-mono text-xs">{w.wpc_no}</td>
                  <td className="text-xs">{formatDate(w.certified_date)}</td>
                  <td className="text-xs">{w.mb_id ? 'MB' : w.milestone_id ? 'Milestone' : 'Manual'}</td>
                  <td className="text-right tabular-nums text-xs">{formatINR(w.gross_certified_value)}</td>
                  <td className="text-right tabular-nums text-xs font-display font-bold">{formatINR(w.net_payable)}</td>
                  <td className="text-right tabular-nums text-xs">{formatINR(w.cumulative_certified)}</td>
                  <td><span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-display font-bold uppercase ${WPC_BADGE[w.status] || ''}`}>{w.status}</span></td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {w.status === 'approved' && (
                      <button onClick={() => raiseBill(w)} disabled={busy}
                        className="px-2 py-1 text-[10px] bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50"
                        data-testid={`wpc-raise-bill-${w.wpc_no}`}>
                        Raise RA Bill
                      </button>
                    )}
                  </td>
                </tr>
                {expanded === w.id && (
                  <tr><td colSpan="8" className="bg-slate-50 px-4 py-3" data-testid={`wpc-waterfall-${w.wpc_no}`}>
                    <div className="max-w-sm space-y-1">
                      <Row label="Gross certified value" value={w.gross_certified_value} bold />
                      <Row label={`− Retention (${w.retention_pct}%)`} value={-w.retention_amount} />
                      <Row label="− Advance recovery" value={-w.advance_recovery_amount} />
                      {w.client_material_deduction > 0 && <Row label="− Client material" value={-w.client_material_deduction} />}
                      {w.power_water_deduction > 0 && <Row label="− Power & water" value={-w.power_water_deduction} />}
                      {w.ld_amount > 0 && <Row label="− LD" value={-w.ld_amount} />}
                      {w.other_deductions > 0 && <Row label="− Other" value={-w.other_deductions} />}
                      <div className="border-t border-slate-300 pt-1"><Row label="= Net payable" value={w.net_payable} bold /></div>
                    </div>
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <div className="kpi-overline flex items-center gap-1.5 mb-2">
          <Receipt size={11} /> Subcontractor RA Bills ({bills.length})
          <button onClick={draftFromBoq} disabled={busy}
            className="ml-auto px-2 py-1 text-[10px] border border-[#3b4872] text-[#3b4872] rounded-sm font-display font-bold hover:bg-indigo-50 disabled:opacity-50"
            title="Compute earned BOQ value minus prior bills and draft the next RA bill"
            data-testid="wo-draft-ra-from-boq">
            Draft from BOQ earned →
          </button>
        </div>
        <table className="w-full dense-table">
          <thead><tr><th>Bill No</th><th>WPC</th><th className="text-right">Gross</th><th className="text-right">Net Payable</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {bills.length === 0 && <tr><td colSpan="6" className="text-center py-8 text-slate-400 text-xs">No RA bills raised</td></tr>}
            {bills.map((b) => (
              <tr key={b.id} data-testid={`rabill-row-${b.bill_no}`}>
                <td className="font-mono text-xs">{b.bill_no}</td>
                <td className="font-mono text-xs">{b.wpc_no || '—'}</td>
                <td className="text-right tabular-nums text-xs">{formatINR(b.gross_value)}</td>
                <td className="text-right tabular-nums text-xs font-display font-bold">{formatINR(b.net_payable)}</td>
                <td><span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-display font-bold uppercase ${BILL_BADGE[b.status] || 'bg-slate-100 text-slate-600'}`}>{b.status}</span></td>
                <td>
                  {b.status === 'Pending Approval' && (
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => billAct(b, 'approve-subcontractor')} disabled={busy} className="px-2 py-1 text-[10px] bg-emerald-600 text-white rounded-sm font-display font-bold disabled:opacity-50" data-testid={`rabill-approve-${b.bill_no}`}>Approve</button>
                      <button onClick={() => { const r = window.prompt('Rejection reason:'); if (r) billAct(b, 'reject-subcontractor', { reason: r }); }} disabled={busy} className="px-2 py-1 text-[10px] bg-red-50 text-red-700 border border-red-200 rounded-sm font-display font-bold disabled:opacity-50">Reject</button>
                    </div>
                  )}
                  {b.rejection_reason && <span className="text-[10px] text-red-600">{b.rejection_reason}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Row = ({ label, value, bold }) => (
  <div className={`flex justify-between text-xs ${bold ? 'font-display font-bold' : 'text-slate-600'}`}>
    <span>{label}</span>
    <span className="tabular-nums">{formatINR(Math.abs(value)) && value < 0 ? `(${formatINR(Math.abs(value))})` : formatINR(value)}</span>
  </div>
);
